import angular from 'angular';
import _ from 'lodash';

import {localeMeta} from '@/languages/meta';
const localeSupported = _.uniq(localeMeta.languages.map(l => l.code));
const importAllLocales = () => import(
    /* webpackChunkName: "locales" */
    '../../languages/index'
);

export const vrmLocalesModule = angular.module('vrm.locales', []);

const localeConf = {
    defaultLocale: 'en',
    persistSelection: true,
    cookieName: 'COOKIE_LOCALE_LANG',
    observableAttrs: new RegExp('^data-(?!ng-|i18n)'),
    delimiter: '::',
    keyNotFound: '%%KEY_NOT_FOUND%%'
};

export const localeEvents = {
    resourceUpdates: 'ngLocalizeResourcesUpdated',
    localeChanges: 'ngLocalizeLocaleChanged'
};

vrmLocalesModule.service('locale',
    /* @ngInject */
    ($injector, $http, $q, $log, $rootScope, $window) => {
        let TOKEN_REGEX = new RegExp('^[\\w\\.-]+\\.[\\w\\s\\.-]+\\w(:.*)?$'),
            currentLocale,
            deferrences,
            bundles,
            cookieStore;

        if (localeConf.persistSelection && $injector.has('$cookies')) {
            cookieStore = $injector.get('$cookies');
        }

        function isToken(str) {
            return (str && str.length && TOKEN_REGEX.test(str));
        }

        function getPath(tok) {
            var path = tok ? tok.split('.') : '',
                result = '';

            if (path.length > 1) {
                result = path.slice(0, -1).join('.');
            }

            return result;
        }

        function getKey(tok) {
            var path = tok ? tok.split('.') : [],
                result = '';

            if (path.length) {
                result = path[path.length - 1];
            }

            return result;
        }

        function getBundle(tok) {
            var result = null,
                path = tok ? tok.split('.') : [],
                i;

            if (path.length > 1) {
                result = bundles;

                for (i = 0; i < path.length - 1; i++) {
                    if (result[path[i]]) {
                        result = result[path[i]];
                    } else {
                        result = null;
                        break;
                    }
                }
            }

            return result;
        }

        async function performImport(language, scope) {
            $log.info(`[localizationService] Loading locale module '${language}/${scope}'`);
            const languages = await importAllLocales();
            return languages.default[`./${language}/${scope}.lang.json`]
        }

        async function loadBundle(token) {
            if (!token.includes('.')) return;
            const [scope] = token.split('.', 1);
            const root = bundles[scope] || (bundles[scope] = {});
            if (root._loading) return;
            root._loading = true;
            let english = null;

            // try loading main language first
            try {
                english = await performImport(localeConf.defaultLocale, scope);
                // Merge the contents of the obtained data into the stored bundle.
                // If the key is missing from the bundle then the english term is merged in
                _.merge(root, english);
                // Notify anyone who cares to know about update event.
                $rootScope.$broadcast(localeEvents.resourceUpdates, bundles);
            } catch(error) {
                $log.error(`[localizationService] Failed to load scope: '${scope}' for '${localeConf.defaultLocale}'. ${error}`);
                setTimeout(() => $window.location.reload(true), 1000);
            }

            try {
                // try loading main data
                const data = currentLocale === localeConf.defaultLocale ? english : await performImport(currentLocale, scope);
                if (!data) {
                    return ;
                }

                // We already merged and broadcasted for localeConf.defaultLocale
                if (currentLocale !== localeConf.defaultLocale) {
                    _.merge(root, data);
                    $rootScope.$broadcast(localeEvents.resourceUpdates, bundles);
                }

                // If we issued a Promise for this file, resolve it now.
                const path = getPath(token);
                if (deferrences[path]) {
                    deferrences[path].resolve(path);
                }
            } catch (error) {
                $log.error(`[localizationService] Failed to load scope: '${scope}' for '${currentLocale}'. ${error}`);
                setTimeout(() => $window.location.reload(true), 1000);
            } finally {
                delete root._loading;
            }
        }

        function bundleReady(path) {
            const token = path + '._LOOKUP_';
            const bundle = getBundle(token);

            if (!deferrences[path]) {
                deferrences[path] = $q.defer();
            }

            if (!bundle) {
                loadBundle(token);
            } else if (!bundle._loading) {
                deferrences[path].resolve(path);
            }

            return deferrences[path].promise;
        }

        function ready(...paths) {
            if (paths.length > 1) {
                return $q.all(paths.map(bundleReady));
            } else {
                return bundleReady(paths[0]);
            }
        }

        function applySubstitutions(text, subs) {
            var res = text,
                firstOfKind = 0;

            if (subs) {
                if (angular.isArray(subs)) {
                    angular.forEach(subs, function (sub, i) {
                        res = res.replace('%' + (i + 1), sub);
                        res = res.replace('{' + (i + 1) + '}', sub);
                    });
                } else {
                    angular.forEach(subs, function (v, k) {
                        ++firstOfKind;

                        res = res.replace('{' + k + '}', v);
                        res = res.replace('%' + k, v);
                        res = res.replace('%' + (firstOfKind), v);
                        res = res.replace('{' + (firstOfKind) + '}', v);
                    });
                }
            }
            res = res.replace(/\n/g, '<br>');

            return res;
        }

        function getLocalizedString(txt, subs, nullable = false) {
            var result = '',
                bundle,
                key,
                A,
                isValidToken = false;

            if (angular.isString(txt) && !subs && txt.indexOf(localeConf.delimiter) !== -1) {
                A = txt.split(localeConf.delimiter);
                txt = A[0];
                subs = angular.fromJson(A[1]);
            }

            isValidToken = isToken(txt);
            if (isValidToken) {
                if (!angular.isObject(subs)) {
                    subs = [subs];
                }

                bundle = getBundle(txt);
                if (bundle && !bundle._loading) {
                    key = getKey(txt);

                    if (bundle[key]) {
                        result = applySubstitutions(bundle[key], subs);
                    } else if (nullable) {
                        result = null;
                    } else {
                        $log.info('[localizationService] Key not found: ' + txt);
                        result =
                            angular.isObject(subs) && subs.i18nFallback !== undefined
                                ? subs.i18nFallback
                                : localeConf.keyNotFound;
                    }
                } else {
                    if (!bundle) {
                        loadBundle(txt);
                    }
                }
            } else {
                result = txt;
            }

            return result;
        }

        function setLocale(value) {
            let lang;

            if (angular.isString(value)) {
                lang = value.trim();
            }

            if (localeSupported.indexOf(lang) === -1) {
                lang = localeConf.defaultLocale;
            }

            if (lang !== currentLocale) {
                // Fix: change locale after everything is loaded
                for (var key in deferrences)
                    deferrences[key].resolve();

                bundles = {};
                deferrences = {};
                currentLocale = lang;

                $rootScope.$broadcast(localeEvents.localeChanges, currentLocale);
                $rootScope.$broadcast(localeEvents.resourceUpdates, bundles);

                if (cookieStore) {
                    cookieStore.put(localeConf.cookieName, lang);
                }
            }
        }

        function getLocale() {
            return currentLocale;
        }

        setLocale(cookieStore ? cookieStore.get(localeConf.cookieName) : $window.navigator.userLanguage || $window.navigator.language);

        return {
            ready,
            isToken,
            getPath,
            getKey,
            setLocale,
            getLocale,
            getString: getLocalizedString,
            keyNotFound: localeConf.keyNotFound
        };
    }
);

vrmLocalesModule.filter('i18n',
    /* @ngInject */
    (locale) => {
        var i18nFilter = function (input, args) {
            return locale.getString(input, args);
        };

        i18nFilter.$stateful = true;

        return i18nFilter;
    }
);

// Same as i18n filter, but if missing results in `null`
vrmLocalesModule.filter('i18nNullable', /* @ngInject */ (locale) => {
    const i18nFilter = (input, args) => locale.getString(input, args, true);
    i18nFilter.$stateful = true;
    return i18nFilter;
});

vrmLocalesModule.directive('i18n',
    /* @ngInject */
    ($sce, locale) => {
        function setText(elm, tag) {
            if (tag !== elm.html()) {
                elm.html($sce.getTrustedHtml(tag));
            }
        }

        function update(elm, string, optArgs) {
            if (locale.isToken(string)) {
                locale.ready(locale.getPath(string)).then(function () {
                    const localeString = locale.getString(string, optArgs);
                    if (localeString !== localeConf.keyNotFound) {
                        setText(elm, localeString);
                    } else if (elm.text().length === 0) {
                        setText(elm, localeConf.keyNotFound);
                    }
                });
            } else {
                setText(elm, string);
            }
        }

        return function (scope, elm, attrs) {
            var hasObservers;

            attrs.$observe('i18n', function (newVal, oldVal) {
                if (newVal && newVal != oldVal) {
                    update(elm, newVal, hasObservers);
                }
            });

            angular.forEach(attrs.$attr, function (attr, normAttr) {
                if (localeConf.observableAttrs.test(attr)) {
                    attrs.$observe(normAttr, function (newVal, oldVal) {
                        if ((newVal && newVal != oldVal) || !hasObservers || !hasObservers[normAttr]) {
                            hasObservers = hasObservers || {};
                            hasObservers[normAttr] = attrs[normAttr];
                            update(elm, attrs.i18n, hasObservers);
                        }
                    });
                }
            });

            scope.$on(localeEvents.resourceUpdates, function () {
                update(elm, attrs.i18n, hasObservers);
            });
            scope.$on(localeEvents.localeChanges, function () {
                update(elm, attrs.i18n, hasObservers);
            });
        };
    }
);

vrmLocalesModule.directive('i18nAttr',
    /* @ngInject */
    (locale) => {
        return function (scope, elem, attrs) {
            var lastValues = {};

            function updateText(target, attributes) {
                var values = scope.$eval(attributes),
                    langFiles = [],
                    exp;

                for (var key in values) {
                    exp = values[key];
                    if (locale.isToken(exp) && langFiles.indexOf(locale.getPath(exp)) === -1) {
                        langFiles.push(locale.getPath(exp));
                    }
                }

                locale.ready(...langFiles).then(function () {
                    var value = '';

                    for (var key in values) {
                        exp = values[key];
                        value = locale.getString(exp);
                        if (lastValues[key] !== value) {
                            attrs.$set(key, lastValues[key] = value);
                        }
                    }
                });
            }

            attrs.$observe('i18nAttr', function (newVal, oldVal) {
                if (newVal && newVal != oldVal) {
                    updateText(elem, newVal);
                }
            });

            scope.$on(localeEvents.resourceUpdates, function () {
                updateText(elem, attrs.i18nAttr);
            });
            scope.$on(localeEvents.localeChanges, function () {
                updateText(elem, attrs.i18nAttr);
            });
        };
    }
);

vrmLocalesModule.constant('localeVer', '1.2.1');
vrmLocalesModule.value('localeSupported', localeSupported);
